// ImportyTab — Nastavenia → Importy (Mirko 2026-05-31)
// Office vedúca: prehľad všetkých importovaných dokladov z MKsoft + manuálne triedenie konfliktov.
// Ústava sek. 7 (MKsoft READ-ONLY) + sek. 12 (žiadna tichá automatika).
import React, { useState, useEffect, useMemo } from 'react'
import { COLORS, TYPO, SPACE, RADIUS, SK, hladaj } from '../theme.js'
import { Tabs, SearchInput, FilterChips, KpiCard, KpiRow } from '../components/Ui.jsx'
import { Btn, BtnAdd, BtnGhost } from '../components/Btn.jsx'
import { Modal } from '../components/Modal.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'

const TYPE_CHIPS = [
  { key: '',       label: 'Všetko' },
  { key: 'CONFLICT', label: 'Konflikty' },
  { key: 'FVSVP',  label: 'FVSVP' },   // backend ukladá typ 'FVSVP' (MK_TYPY FVSP→FVSVP); mŕtvy tab 'FVSP' zrušený 2026-06-01 (vracal 0)
  { key: 'FVO',    label: 'FVO' },
  { key: 'FVE',    label: 'FVE' },
  { key: 'VSZAK',  label: 'VSZAK' },
  { key: 'VSOST',  label: 'VSOST' },
]

const STATUS_BADGE = {
  ok:        { label: '✓ OK',      color: '#41694C', bg: '#dcfce7' },
  no_items:  { label: 'bez položiek', color: '#92400e', bg: '#fef3c7' },
  conflict:  { label: 'Konflikt', color: '#8C2F12', bg: '#fef2f2' },
}

export default function ImportyTab() {
  const toast = useToast()
  const [stats, setStats] = useState(null)
  const [filter, setFilter] = useState('')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [drilldown, setDrilldown] = useState(null) // {source, id, mksoft_cislo}

  const reload = async () => {
    setLoading(true)
    try {
      const [s, l] = await Promise.all([
        fetchAuth('/api/imports/stats').then(r => r.ok ? r.json() : {}),
        fetchAuth('/api/imports/log' + (filter ? `?type=${filter}` : '')).then(r => r.ok ? r.json() : []),
      ])
      setStats(s)
      setRows(Array.isArray(l) ? l : [])
    } catch (e) { toast.error('Chyba načítania: ' + e.message) }
    finally { setLoading(false) }
  }
  useEffect(() => { reload() }, [filter])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(r =>
      hladaj((r.mksoft_cislo || ''), q) ||
      hladaj((r.partner || ''), q) ||
      hladaj((r.zakazka_code || ''), q) ||
      hladaj((r.project_name || ''), q) ||
      hladaj((r.matrix_id || ''), q)
    )
  }, [rows, search])

  const niceDate = (s) => {
    if (!s) return '—'
    const m = String(s).match(/(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}:\d{2}))?/)
    return m ? `${m[3]}.${m[2]}.${m[1]}${m[4] ? ' ' + m[4] : ''}` : s
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>

      {/* ── Import na odsúhlasenie (ústava 12.10) ── */}
      <InboxPanel />

      {/* ── KPI súhrn ── */}
      {stats && (
        <KpiRow cols={6}>
          <KpiCard title="Posledný úspešný sync" value={niceDate(stats.lastSuccessSync)}
            color={stats.lastSuccessSync ? COLORS.add : COLORS.remove} />
          <KpiCard title="Posledný pokus" value={niceDate(stats.lastSync)} color={COLORS.muted} />
          <KpiCard title="Konflikty" value={stats.conflictsPending} color={stats.conflictsPending > 0 ? COLORS.remove : COLORS.muted} />
          <KpiCard title="FVSP/FVSVP" value={(stats.counts?.FVSP || 0) + (stats.counts?.FVSVP || 0)} color={COLORS.nav} />
          <KpiCard title="FVO + FVE" value={(stats.counts?.FVO || 0) + (stats.counts?.FVE || 0)} color={COLORS.nav} />
          <KpiCard title="Sync behov spolu" value={stats.totalSyncRuns} color={COLORS.muted} />
        </KpiRow>
      )}

      {/* Warning ak posledný sync je starší ako 24h */}
      {stats?.lastSuccessSync && (Date.now() - new Date(stats.lastSuccessSync.replace(' ', 'T') + 'Z').getTime() > 24 * 3600 * 1000) && (
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: RADIUS.md,
          padding: SPACE.md, fontSize: TYPO.sizes.sm, color: '#92400e' }}>
          <strong>Pozor:</strong> Posledný úspešný sync z MKsoft bol {niceDate(stats.lastSuccessSync)}.
          Skontroluj Python skript na MKsoft serveri (192.168.88.250) — možno nebeží / padol.
        </div>
      )}

      {/* ── F3: Pravidlá auto-priradenia ── */}
      <RulesPanel />

      {/* ── Filter chips ── */}
      <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterChips options={TYPE_CHIPS} value={filter} onChange={setFilter} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Hľadať (číslo / partner / zákazka)…" />
        </div>
        <BtnGhost onClick={reload}>Obnoviť</BtnGhost>
      </div>

      {/* ── Tabuľka ── */}
      <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
          <thead>
            <tr style={{ background: COLORS.bgSoft, borderBottom: `1px solid ${COLORS.line}` }}>
              {['Sync čas', 'MKsoft č.', 'Matrix ID', 'Typ', 'Dátum', 'Partner', 'Č. zákazky', 'Názov zákazky', 'Suma', 'Položky', 'Status'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left',
                  fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
                  color: COLORS.muted, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>Načítavam…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={11} style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>
                {search ? 'Žiadne výsledky vyhľadávania.' : 'Žiadne importy v tejto kategórii.'}
              </td></tr>
            ) : filtered.map((r, i) => {
              const badge = STATUS_BADGE[r.status] || { label: r.status, color: COLORS.muted, bg: COLORS.bgSoft }
              const canDrill = r.source === 'invoices' || r.source === 'expenses'
              return (
                <tr key={i}
                  onClick={() => canDrill && setDrilldown({ source: r.source, id: r.matrix_id, mksoft_cislo: r.mksoft_cislo })}
                  style={{
                    borderBottom: `1px solid ${COLORS.lineSoft || COLORS.line}`,
                    cursor: canDrill ? 'pointer' : 'default',
                    background: i % 2 ? COLORS.bgSoft : 'transparent',
                  }}>
                  <td style={cell}>{niceDate(r.sync_at)}</td>
                  <td style={{ ...cell, fontFamily: TYPO.mono, fontWeight: 700 }}>{r.mksoft_cislo || '—'}</td>
                  <td style={{ ...cell, fontFamily: TYPO.mono, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{r.matrix_id || '—'}</td>
                  <td style={cell}><span style={{ background: COLORS.bgSoft, padding: '2px 8px', borderRadius: 4, fontSize: TYPO.sizes.xs, fontWeight: 600 }}>{r.typ}</span></td>
                  <td style={cell}>{niceDate(r.doc_date)}</td>
                  <td style={{ ...cell, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.partner}</td>
                  <td style={{ ...cell, fontFamily: TYPO.mono, fontWeight: 600 }}>{r.zakazka_code || '—'}</td>
                  <td style={{ ...cell, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: r.project_name ? COLORS.text : COLORS.muted }}>{r.project_name || (r.project_id ? `(${r.project_id})` : '— nepárované —')}</td>
                  <td style={{ ...cell, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 700 }}>{SK.eur(r.amount || 0)}</td>
                  <td style={{ ...cell, textAlign: 'center' }}>{r.items_count ?? '—'}</td>
                  <td style={cell}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: TYPO.sizes.xs, fontWeight: 600,
                    color: badge.color, background: badge.bg }}>{badge.label}</span></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, padding: '4px 8px' }}>
        Zobrazených {filtered.length} z {rows.length} riadkov · MKsoft → Matrix iba čítanie (ústava sek. 7) · Klik na riadok → položky dokladu
      </div>

      {/* ── Drill-down modal ── */}
      {drilldown && (
        <DrilldownModal {...drilldown} onClose={() => setDrilldown(null)} />
      )}
    </div>
  )
}

// ── Drill-down: položky dokladu (FAKTPOHYBY pre FA / položky výdajky) ──
function DrilldownModal({ source, id, mksoft_cislo, onClose }) {
  const toast = useToast()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [projects, setProjects] = useState([])
  const [curProj, setCurProj] = useState('')
  const [assigning, setAssigning] = useState(false)

  const loadDoc = () => {
    setLoading(true)
    fetchAuth(`/api/imports/${source}/${id}/items`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { setData(d); setCurProj(d?.head?.project_id || d?.head?.projectId || '') })
      .catch(e => { toast.error('Chyba: ' + e); setData(null) })
      .finally(() => setLoading(false))
  }
  useEffect(() => { loadDoc() }, [source, id])
  useEffect(() => { fetchAuth('/api/projects').then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {}) }, [])
  const doAssign = async () => {
    setAssigning(true)
    try {
      const r = await fetchAuth(`/api/imports/${source}/${id}/assign`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ project_id: curProj || null }) })
      if (!r.ok) throw new Error('HTTP ' + r.status)
      toast.success('✓ Zákazka priradená')
      loadDoc()
    } catch (e) { toast.error('Chyba: ' + e.message) }
    finally { setAssigning(false) }
  }

  return (
    <Modal open onClose={onClose} title={`Položky dokladu ${mksoft_cislo || id}`} width={900}>
      {loading ? (
        <div style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>Načítavam…</div>
      ) : !data ? (
        <div style={{ padding: SPACE.lg, color: COLORS.remove }}>Doklad sa nepodarilo načítať.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
          {/* Hlavička */}
          <div style={{ background: COLORS.bgSoft, padding: SPACE.md, borderRadius: RADIUS.md,
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: SPACE.sm, fontSize: TYPO.sizes.sm }}>
            <Field label="MKsoft č." v={data.head.mksoft_cislo || data.head.doc_number || mksoft_cislo} />
            <Field label="Dátum" v={data.head.date_issued || data.head.date} />
            <Field label="Partner" v={data.head.customer_name || data.head.supplier || '—'} />
            <Field label="Č. zákazky / Názov" v={
              <span>
                <span style={{ fontFamily: TYPO.mono, fontWeight: 700 }}>{data.head.project_zakazka_code || '—'}</span>
                {data.head.project_name && <span style={{ color: COLORS.muted }}> · {data.head.project_name}</span>}
              </span>
            } />
            <Field label="Suma bez DPH" v={SK.eur(data.head.amount_excl_vat || (data.head.amount - (data.head.amount_vat || 0)) || data.head.amount || 0)} />
            <Field label="DPH" v={SK.eur(data.head.amount_vat || 0)} />
            <Field label="Suma s DPH" v={SK.eur(data.head.amount_total || data.head.amount || 0)} />
            <Field label="Položiek" v={data.items_count} />
          </div>

          {/* ── Manuálne priradenie zákazky (F1) ── */}
          <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap', padding: SPACE.md, background: '#FBFAF7', border: '1px solid #15171A', borderRadius: RADIUS.md }}>
            <span style={{ fontWeight: 700, color: '#b45309' }}>Priradiť zákazku:</span>
            <select value={curProj} onChange={e => setCurProj(e.target.value)} style={{ padding: '6px 9px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, minWidth: 280 }}>
              <option value="">— nepriradené —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{(p.zakazka_code || p.id) + ' · ' + (p.name || '')}</option>)}
            </select>
            <BtnAdd onClick={doAssign} disabled={assigning}>{assigning ? 'Ukladám…' : '✓ Priradiť'}</BtnAdd>
            <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginLeft: 'auto' }}>aktuálne: <b style={{ color: data.head.project_name ? COLORS.text : COLORS.remove }}>{data.head.project_name || '— nepriradené —'}</b></span>
          </div>

          {/* Položky tabuľka */}
          <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
              <thead>
                <tr style={{ background: COLORS.bgSoft, borderBottom: `1px solid ${COLORS.line}` }}>
                  {['#', 'PLU', 'Popis', 'Množstvo', 'MJ', 'Cena/MJ', 'Spolu'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left',
                      fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.muted, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted, fontStyle: 'italic' }}>
                    Doklad nemá importované položky.
                    {source === 'invoices' && ' (FAKTPOHYBY sync ešte nefunguje — Python skript nezahŕňa riadky FA.)'}
                  </td></tr>
                ) : data.items.map((it, i) => (
                  <tr key={it.id} style={{ borderBottom: `1px solid ${COLORS.lineSoft || COLORS.line}`,
                    background: i % 2 ? COLORS.bgSoft : 'transparent' }}>
                    <td style={cell}>{i + 1}</td>
                    <td style={{ ...cell, fontFamily: TYPO.mono, fontSize: TYPO.sizes.xs }}>{it.plu || '—'}</td>
                    <td style={cell}>{it.name}</td>
                    <td style={{ ...cell, textAlign: 'right', fontFamily: TYPO.mono }}>{(it.qty || 0).toFixed(2)}</td>
                    <td style={cell}>{it.unit || '—'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontFamily: TYPO.mono }}>{SK.eur(it.price_unit || 0)}</td>
                    <td style={{ ...cell, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 700 }}>{SK.eur(it.amount_total || it.price_total || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <BtnGhost onClick={onClose}>Zavrieť</BtnGhost>
          </div>
        </div>
      )}
    </Modal>
  )
}

function Field({ label, v }) {
  return (
    <div>
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.text, marginTop: 2 }}>{v ?? '—'}</div>
    </div>
  )
}

const cell = { padding: '8px 10px', fontSize: TYPO.sizes.sm, color: COLORS.text }

// ── RulesPanel (F3) — pravidlá auto-priradenia zákazky podľa typu/série dokladu ──
function RulesPanel() {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [rules, setRules] = useState([])
  const [projects, setProjects] = useState([])
  const [kind, setKind] = useState('type')
  const [matchVal, setMatchVal] = useState('')
  const [proj, setProj] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => {
    fetchAuth('/api/import-rules').then(r => r.ok ? r.json() : []).then(d => setRules(Array.isArray(d) ? d : [])).catch(() => {})
  }
  useEffect(() => { load(); fetchAuth('/api/projects').then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {}) }, [])

  const add = async () => {
    if (!matchVal.trim() || !proj) { toast.error('Vyplň hodnotu a zákazku'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/import-rules', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, match_value: matchVal.trim(), project_id: proj }) })
      if (!r.ok) throw new Error('HTTP ' + r.status)
      toast.success('Pravidlo pridané')
      setMatchVal(''); setProj(''); load()
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }
  const toggle = async (ru) => {
    await fetchAuth('/api/import-rules/' + ru.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: ru.active ? 0 : 1 }) })
    load()
  }
  const del = async (ru) => {
    if (!window.confirm('Zmazať pravidlo?')) return
    await fetchAuth('/api/import-rules/' + ru.id, { method: 'DELETE' })
    toast.success('Pravidlo zmazané'); load()
  }
  const applyExisting = async () => {
    setBusy(true)
    try {
      const r = await fetchAuth('/api/import-rules/apply-existing', { method: 'POST' })
      const j = await r.json()
      toast.success(`Priradené: ${j.invoices_assigned || 0} faktúr, ${j.expenses_assigned || 0} výdavkov`)
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }

  return (
    <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: SPACE.md, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: SPACE.sm, background: COLORS.bgSoft }}>
        <span style={{ color: COLORS.nav, fontWeight: 700 }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontWeight: TYPO.weights.bold }}>Pravidlá auto-priradenia</span>
        <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>· {rules.length} pravidiel · budúce importy sa priradia automaticky podľa typu/série</span>
      </div>
      {open && (
        <div style={{ padding: SPACE.md, display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
          {/* pridanie */}
          <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={kind} onChange={e => setKind(e.target.value)} style={selStyle}>
              <option value="type">Typ dokladu (presná zhoda)</option>
              <option value="series">Séria — prefix čísla dokladu</option>
            </select>
            <input value={matchVal} onChange={e => setMatchVal(e.target.value)} placeholder={kind === 'type' ? 'napr. VSOST, FVO…' : 'napr. 26, 2600…'}
              style={{ ...selStyle, width: 160 }} />
            <span style={{ color: COLORS.muted }}>→</span>
            <select value={proj} onChange={e => setProj(e.target.value)} style={{ ...selStyle, minWidth: 260 }}>
              <option value="">— vyber zákazku —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{(p.zakazka_code || p.id) + ' · ' + (p.name || '')}</option>)}
            </select>
            <BtnAdd onClick={add} disabled={busy}>+ Pridať</BtnAdd>
            <BtnGhost onClick={applyExisting} disabled={busy}>↻ Aplikovať na nepriradené</BtnGhost>
          </div>
          {/* zoznam */}
          {rules.length === 0 ? (
            <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm, fontStyle: 'italic' }}>Žiadne pravidlá. Pridaj prvé hore.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
              <thead><tr style={{ background: COLORS.bgSoft, borderBottom: `1px solid ${COLORS.line}`, color: COLORS.muted }}>
                <th style={cell}>Druh</th><th style={cell}>Hodnota</th><th style={cell}>Zákazka</th><th style={cell}>Stav</th><th style={cell}></th>
              </tr></thead>
              <tbody>
                {rules.map(ru => (
                  <tr key={ru.id} style={{ borderBottom: `1px solid ${COLORS.lineSoft || COLORS.line}`, opacity: ru.active ? 1 : 0.5 }}>
                    <td style={cell}>{ru.kind === 'type' ? 'Typ' : 'Séria'}</td>
                    <td style={{ ...cell, fontFamily: TYPO.mono, fontWeight: 700 }}>{ru.match_value}</td>
                    <td style={cell}>{(ru.zakazka_code || ru.project_id)}{ru.project_name ? ` · ${ru.project_name}` : ''}</td>
                    <td style={cell}>
                      <span onClick={() => toggle(ru)} style={{ cursor: 'pointer', color: ru.active ? COLORS.add : COLORS.muted, fontWeight: 700 }}>
                        {ru.active ? '✓ aktívne' : '○ vypnuté'}
                      </span>
                    </td>
                    <td style={cell}><span onClick={() => del(ru)} style={{ cursor: 'pointer', color: COLORS.remove }}>Zmazať</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

const selStyle = { padding: '7px 9px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm }

// ── InboxPanel (ústava 12.10) — „Import na odsúhlasenie": checkbox výber + Potvrď/Oprav/Zamietni + Potvrď všetky OK ──
function InboxPanel() {
  const toast = useToast()
  const [data, setData] = useState({ rows: [], count: 0, ok_count: 0 })
  const [sel, setSel] = useState({})
  const [busy, setBusy] = useState(false)
  const [projects, setProjects] = useState([])
  const [reassign, setReassign] = useState(null)
  const [exp, setExp] = useState(null)        // rozbalený riadok (key)
  const [itemsCache, setItemsCache] = useState({})
  const toggleExp = async (r) => {
    const k = `${r.source}:${r.id}`
    if (exp === k) { setExp(null); return }
    setExp(k)
    if (!itemsCache[k]) {
      setItemsCache(c => ({ ...c, [k]: { loading: true } }))
      try { const d = await (await fetchAuth(`/api/imports/${r.source}/${r.id}/items`)).json(); setItemsCache(c => ({ ...c, [k]: { items: Array.isArray(d?.items) ? d.items : (Array.isArray(d) ? d : []) } })) }
      catch (e) { setItemsCache(c => ({ ...c, [k]: { items: [], error: e.message } })) }
    }
  }

  const load = () => fetchAuth('/api/imports/inbox').then(r => r.ok ? r.json() : { rows: [] }).then(d => { setData(d); setSel({}) }).catch(() => {})
  useEffect(() => { load(); fetchAuth('/api/projects').then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {}) }, [])

  const key = r => `${r.source}:${r.id}`
  const selItems = () => data.rows.filter(r => sel[key(r)]).map(r => ({ source: r.source, id: r.id }))
  const toggle = r => setSel(s => ({ ...s, [key(r)]: !s[key(r)] }))
  const allSel = data.rows.length > 0 && data.rows.every(r => sel[key(r)])
  const toggleAll = () => setSel(allSel ? {} : Object.fromEntries(data.rows.map(r => [key(r), true])))

  const doConfirm = async (items) => {
    if (!items.length) { toast.error('Nič nevybrané'); return }
    setBusy(true)
    try { const r = await fetchAuth('/api/imports/inbox/confirm', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) }); const j = await r.json(); toast.success(`Potvrdené: ${j.confirmed}`); load() }
    catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }
  const confirmAllOk = async () => {
    setBusy(true)
    try { const r = await fetchAuth('/api/imports/inbox/confirm', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ all_ok: true }) }); const j = await r.json(); toast.success(`Potvrdené všetky OK: ${j.confirmed}`); load() }
    catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }
  const relinkCustomers = async () => {
    setBusy(true)
    try { const r = await fetchAuth('/api/imports/relink-customers', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); const j = await r.json(); if (!r.ok) throw new Error(j.error || 'Zlyhalo'); const sk = (j.skipped || []).length; toast.success(`Dohľadané partneri: napojených ${j.linked}, nových ${j.created}` + (sk ? ` · ${sk} bez IČO (doplň ručne)` : '')); load() }
    catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }
  const doReject = async (items) => {
    if (!items.length) { toast.error('Nič nevybrané'); return }
    if (!window.confirm(`Zamietnuť ${items.length} dokladov? (archivuje sa, vrátiteľné cez LOG)`)) return
    setBusy(true)
    try { const r = await fetchAuth('/api/imports/inbox/reject', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) }); const j = await r.json(); toast.success(`Zamietnuté: ${j.rejected}`); load() }
    catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }
  const doReassign = async (row, projectId) => {
    setBusy(true)
    try { await fetchAuth(`/api/imports/${row.source}/${row.id}/assign`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ project_id: projectId || null }) }); toast.success('Cieľ opravený'); setReassign(null); load() }
    catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }

  if (data.count === 0) return (
    <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: RADIUS.md, padding: SPACE.md, fontSize: TYPO.sizes.sm, color: '#41694C' }}>
      ✓ Žiadne importy nečakajú na odsúhlasenie — všetko spracované.
    </div>
  )

  const STAV = { OK: { c: '#41694C', b: '#dcfce7', l: 'OK' }, SKONTROLUJ: { c: '#b45309', b: '#fef3c7', l: 'SKONTROLUJ' }, KONFLIKT: { c: COLORS.remove, b: '#fee2e2', l: 'KONFLIKT' }, 'DUPLIKAT': { c: '#7c3aed', b: '#ede9fe', l: 'DUPLIKÁT' } }
  const td = { padding: '7px 9px', fontSize: TYPO.sizes.sm, borderBottom: `1px solid ${COLORS.lineSoft || COLORS.line}` }

  return (
    <div style={{ background: COLORS.bg, border: `2px solid #15171A`, borderRadius: RADIUS.lg, overflow: 'hidden' }}>
      <div style={{ padding: SPACE.md, background: '#FBFAF7', display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.md, color: '#b45309' }}>Import na odsúhlasenie</span>
        <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>· {data.count} dokladov · {data.ok_count} OK · neovplyvnia náklady kým nepotvrdíš</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' }}>
          <BtnGhost onClick={relinkCustomers} disabled={busy} title="Pre doklady bez zákazníka dotiahne názov z ORSR/RPO podľa IČO a založí/napojí partnera">Dohľadať partnerov</BtnGhost>
          <BtnAdd onClick={confirmAllOk} disabled={busy || data.ok_count === 0}>✓ Potvrď všetky OK ({data.ok_count})</BtnAdd>
          <Btn onClick={() => doConfirm(selItems())} disabled={busy}>✓ Potvrď vybrané</Btn>
          <BtnGhost onClick={() => doReject(selItems())} disabled={busy}>✗ Zamietni vybrané</BtnGhost>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
          <thead><tr style={{ background: COLORS.bgSoft, borderBottom: `1px solid ${COLORS.line}` }}>
            <th style={{ ...td, width: 28 }}><input type="checkbox" checked={allSel} onChange={toggleAll} /></th>
            {['Čo', 'Kam', 'Doklad', 'Dátum', 'Dodávateľ', 'Suma €', 'Zákazka', 'Stav', ''].map(h => <th key={h} style={{ ...td, textAlign: 'left', color: COLORS.muted, fontWeight: TYPO.weights.semibold, fontSize: TYPO.sizes.xs, textTransform: 'uppercase' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {data.rows.map(r => {
              const st = STAV[r.stav] || STAV.OK
              const k = key(r); const isOpen = exp === k; const cache = itemsCache[k]
              const clickCell = { ...td, cursor: 'pointer' }
              return (
                <React.Fragment key={k}>
                <tr style={{ background: sel[k] ? '#FBFAF7' : (isOpen ? COLORS.bgSoft : 'transparent') }}>
                  <td style={td}><input type="checkbox" checked={!!sel[k]} onChange={() => toggle(r)} /></td>
                  <td style={clickCell} onClick={() => toggleExp(r)}><span style={{ color: COLORS.nav, marginRight: 4 }}>{isOpen ? '▾' : '▸'}</span>{r.co}</td>
                  <td style={{ ...clickCell, color: COLORS.muted }} onClick={() => toggleExp(r)}>{r.kam}</td>
                  <td style={{ ...clickCell, fontFamily: TYPO.mono, fontWeight: 700 }} onClick={() => toggleExp(r)}>{r.doklad}</td>
                  <td style={clickCell} onClick={() => toggleExp(r)}>{r.datum}</td>
                  <td style={{ ...clickCell, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={() => toggleExp(r)}>{r.dodavatel || '—'}</td>
                  <td style={{ ...clickCell, textAlign: 'right', fontFamily: TYPO.mono }} onClick={() => toggleExp(r)}>{SK.eur(r.suma || 0)}</td>
                  <td style={clickCell} onClick={() => toggleExp(r)}>{r.zakazka || <span style={{ color: COLORS.remove }}>— chýba —</span>}</td>
                  <td style={td}><span title={r.dovod || ''} style={{ background: st.b, color: st.c, padding: '2px 8px', borderRadius: 10, fontSize: TYPO.sizes.xs, fontWeight: 700, cursor: r.dovod ? 'help' : 'default' }}>{st.l}{r.dovod ? ' ⓘ' : ''}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <span onClick={() => doConfirm([{ source: r.source, id: r.id }])} style={{ cursor: 'pointer', color: '#41694C', marginRight: 8, fontWeight: 700 }} title="Potvrď">✓</span>
                    <span onClick={() => setReassign(r)} style={{ cursor: 'pointer', color: COLORS.nav, marginRight: 8 }} title="Oprav cieľ (zákazka)">✎</span>
                    <span onClick={() => doReject([{ source: r.source, id: r.id }])} style={{ cursor: 'pointer', color: COLORS.remove }} title="Zamietni">✗</span>
                  </td>
                </tr>
                {isOpen && (
                  <tr><td colSpan={10} style={{ padding: 0, background: COLORS.bgSoft, borderBottom: `2px solid ${COLORS.line}` }}>
                    <div style={{ padding: SPACE.md }}>
                      {r.dovod && (
                        <div style={{ marginBottom: SPACE.sm, padding: SPACE.sm, background: st.b, borderRadius: RADIUS.sm, color: st.c, fontSize: TYPO.sizes.sm, fontWeight: 600 }}>
                          {r.stav === 'KONFLIKT' ? 'Dôvod konfliktu: ' : ''}{r.dovod}
                        </div>
                      )}
                      <div style={{ fontSize: TYPO.sizes.xs, fontWeight: 700, color: COLORS.muted, marginBottom: 4 }}>POLOŽKY DOKLADU</div>
                      {!cache || cache.loading ? <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>Načítavam…</div>
                        : cache.error ? <div style={{ color: COLORS.remove, fontSize: TYPO.sizes.xs }}>Chyba: {cache.error}</div>
                        : !cache.items?.length ? <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs, fontStyle: 'italic' }}>Doklad nemá rozpísané položky.</div>
                        : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.xs }}>
                            <thead><tr style={{ color: COLORS.muted }}>
                              <th style={{ padding: '3px 8px', textAlign: 'left' }}>PLU</th><th style={{ padding: '3px 8px', textAlign: 'left' }}>Názov</th>
                              <th style={{ padding: '3px 8px', textAlign: 'right' }}>Množ.</th><th style={{ padding: '3px 8px', textAlign: 'left' }}>MJ</th>
                              <th style={{ padding: '3px 8px', textAlign: 'right' }}>J. cena</th><th style={{ padding: '3px 8px', textAlign: 'right' }}>Spolu</th>
                            </tr></thead>
                            <tbody>{cache.items.map((it, ix) => (
                              <tr key={ix} style={{ borderTop: `1px solid ${COLORS.line}` }}>
                                <td style={{ padding: '3px 8px', fontFamily: TYPO.mono, color: COLORS.muted }}>{it.plu || '—'}</td>
                                <td style={{ padding: '3px 8px' }}>{it.name || '—'}</td>
                                <td style={{ padding: '3px 8px', textAlign: 'right', fontFamily: TYPO.mono }}>{it.quantity ?? it.qty ?? '—'}</td>
                                <td style={{ padding: '3px 8px' }}>{it.unit || '—'}</td>
                                <td style={{ padding: '3px 8px', textAlign: 'right', fontFamily: TYPO.mono }}>{SK.eur(it.unit_price ?? it.price_unit ?? 0)}</td>
                                <td style={{ padding: '3px 8px', textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 600 }}>{SK.eur(it.total_price ?? it.price_total ?? 0)}</td>
                              </tr>
                            ))}</tbody>
                          </table>
                        )}
                    </div>
                  </td></tr>
                )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {reassign && (
        <Modal open onClose={() => setReassign(null)} title={`Oprav cieľ — ${reassign.doklad}`}
          footer={<BtnGhost onClick={() => setReassign(null)}>Zavrieť</BtnGhost>}>
          <div style={{ marginBottom: SPACE.sm, fontSize: TYPO.sizes.sm, color: COLORS.muted }}>Priraď zákazku k dokladu {reassign.co} {reassign.doklad}:</div>
          <select defaultValue={reassign.project_id || ''} onChange={e => doReassign(reassign, e.target.value)} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm }}>
            <option value="">— nepriradené —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{(p.zakazka_code || p.id) + ' · ' + (p.name || '')}</option>)}
          </select>
        </Modal>
      )}
    </div>
  )
}
